import type { ReactNode } from 'react'
import { StatusPill } from '@/components/status-pill'
import type { components } from '@/api/schema'
import { cn } from '@/lib/utils'

type StatusChip = components['schemas']['StatusChip']

/** Order, Shipping, AP and AR statuses worked out from a PO's or project's items (USA-1202). */
export function OrderStatusChips({
  order,
  shipping,
  ap,
  ar,
  compact = false,
  className,
  wrapOrder,
}: {
  order?: StatusChip | null
  shipping?: StatusChip | null
  ap?: StatusChip | null
  ar?: StatusChip | null
  /** Pills only; the labels stay for screen readers. */
  compact?: boolean
  className?: string
  /** Wraps the Order pill, e.g. in the trigger that corrects the status (USA-1432). */
  wrapOrder?: (pill: ReactNode) => ReactNode
}) {
  const entries: [string, StatusChip][] = []
  for (const [label, chip] of [
    ['Order', order],
    // A replaced PO says so once, on its Order pill (USA-1689).
    ['Shipping', shipping?.key === 'replaced' ? null : shipping],
    ['AP', ap],
    ['AR', ar],
  ] as const) {
    if (chip) entries.push([label, chip])
  }
  if (!entries.length) return null
  return (
    <dl
      className={cn(
        'flex flex-wrap items-center gap-y-1',
        compact ? 'gap-x-1.5' : 'gap-x-3',
        className,
      )}
    >
      {entries.map(([label, chip]) => (
        <div key={label} className="flex items-center gap-1.5">
          <dt className={compact ? 'sr-only' : 'text-xs text-muted-foreground'}>{label}</dt>
          <dd>
            {label === 'Order' && wrapOrder ? (
              wrapOrder(<StatusPill tone={chip.tone}>{chip.label}</StatusPill>)
            ) : (
              <StatusPill tone={chip.tone}>{chip.label}</StatusPill>
            )}
          </dd>
        </div>
      ))}
    </dl>
  )
}
